<template>
	<div
		v-cloak
		class="d-flex flex-column dashed-style amelipro-upload"
		@dragover.prevent
		@drop.prevent="onDropFile"
	>
		<div class="d-flex align-center justify-space-between w-100">
			<VFileInput
				:id="uniqueId"
				ref="vFileInput"
				:accept="fileTypeAccepted.join(',')"
				:aria-describedby="describedby"
				:aria-invalid="!valid"
				:aria-required="ariaRequired"
				dense
				hide-details
				multiple
				:prepend-icon="null"
				:rules="inputRules"
				:value="fileInput"
				@blur="onBlur"
				@change="onChange"
				@focus="onFocus"
				@update:error="importErrorMessages = []"
			>
				<template #append>
					<label
						class="d-flex align-center w-100 amelipro-upload__label"
						:class="focused ? 'label-outline' : undefined"
						:for="uniqueId"
					>
						<AmeliproIcon
							class="mr-2 amelipro-upload__icon"
							icon="pieceJointe"
							icon-bg-color="ap-blue darken-1"
							icon-color="ap-white"
							x-large
						/>

						<span class="d-flex justify-space-between align-center w-100">
							<span class="text-body-1 upload-input-text text-left ap-grey--text text--darken-1 amelipro-upload__text">
								{{ inputLabel }}
								<span v-if="ariaRequired">
									<span aria-hidden="true">
										&nbsp;*
									</span>

									<span class="d-sr-only">
										&nbsp;Champ obligatoire
									</span>
								</span>
							</span>

							<slot name="append-icon">
								<img
									alt=""
									class="ml-4 upload-input-img"
									src="../../assets/img/bg-pieces-jointe.svg"
								>
							</slot>
						</span>
					</label>
				</template>
			</VFileInput>
		</div>

		<div
			v-if="hasError"
			class="d-flex"
		>
			<AmeliproMessage
				:id="errorId"
				class="my-2"
				text
				type="error"
			>
				<ul
					v-if="displayedErrorMessages.length > 1"
					class="mb-0"
				>
					<li
						v-for="(errorMessage, index) in displayedErrorMessages"
						:key="index"
					>
						{{ errorMessage }}
					</li>
				</ul>
				<p
					v-else
					class="mb-0"
				>
					{{ displayedErrorMessages[0] }}
				</p>
			</AmeliproMessage>
		</div>

		<div
			v-if="fileInput.length > 0"
			class="d-flex mt-2"
		>
			<ul class="list-style-none amelipro-upload__list">
				<li
					v-for="(file, index) in fileInput"
					:key="index"
					class="mt-6 d-inline-flex amelipro-upload__item"
				>
					<AmeliproChips
						:text="file.name"
						:unique-id="`${uniqueId}-chips-${index}`"
						@click="removeFile(index)"
					/>
				</li>
			</ul>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ErrorMessages, IFileUploadRuleParams, ValidationResult, ValidationRule } from '../../rules/types';
	import { PropType, computed, ref, watch } from 'vue';
	import { fileUploadDuplicationRuleFn, fileUploadFormatRuleFn, fileUploadMaxFileNumberRuleFn, fileUploadRuleFn } from '../../rules/uploadFile';
	import AmeliproIcon from '../AmeliproIcon/AmeliproIcon.vue';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IVFileInput } from './type';
	import { VFileInput } from 'vuetify/lib';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		errorMessages: {
			type: Object as PropType<ErrorMessages>,
			default: undefined,
		},

		/**
		 * @deprecated
		 * Remplacé par errorMessages
		 */
		errorTypeString: {
			type: String,
			default: undefined,
		},
		fileTypeAccepted: {
			type: Array as PropType<string[]>,
			required: true,
		},
		inputLabel: {
			type: String,
			default: 'Cliquer sur ce bouton pour joindre vos documents ou les glisser-déposer directement dans ce cadre',
		},
		maxFileNumber: {
			type: Number,
			default: 1,
		},
		rules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		uniqueId: {
			type: String,
			required: true,
		},
		value: {
			type: Array as PropType<File[]>,
			default: () => [],
		},
	});

	const focused = ref(false);
	const valid = ref(true);
	const vFileInput = ref<IVFileInput>({ valid: true, messagesToDisplay: [] });

	/**
	 * Règles transmises au composant VFileInput
	 */
	const inputRules = computed<ValidationRule[]>(() => {
		//const { rules } = props; => provoque des mutations indésirables de props.rules
		const rules = [
			...props.rules,
		];

		if (props.ariaRequired) {
			rules.push(required);
			rules.push((): ValidationResult => fileUploadMaxFileNumberRuleFn(fileInput.value, props.maxFileNumber));
			rules.push((): ValidationResult => fileUploadDuplicationRuleFn(fileInput.value));
			rules.push((): ValidationResult => fileUploadFormatRuleFn(fileInput.value, props.fileTypeAccepted));
		}

		return rules;
	});

	const emit = defineEmits(['input', 'change']);

	const describedby = computed<string | undefined>(() => (hasError.value ? errorId.value : undefined));
	const messagesToDisplay = computed<string[]>(() => vFileInput.value.messagesToDisplay || []);
	const importErrorMessages = ref<string[]>([]);
	const displayedErrorMessages = computed<string[]>(() => [...messagesToDisplay.value, ...importErrorMessages.value]);
	const errorId = computed<string>(() => `${props.uniqueId}-error`);
	const hasError = computed<boolean>(() => displayedErrorMessages.value.length > 0);

	// v-model component
	const fileInput = computed({
		get: (): File[] => props.value,
		set: (fileList: File[]): void => {
			emit('input', fileList);
		},
	});

	const onDropFile = (dragEvent: DragEvent): void => {
		const droppedFiles = dragEvent?.dataTransfer?.files;
		if (droppedFiles) {
			importErrorMessages.value = [];
			importFileList(Array.from(droppedFiles));
		}
	};

	const removeFile = (index: number): void => {
		importErrorMessages.value = [];
		fileInput.value.splice(index, 1);
		emit('input', fileInput.value);
	};

	const importFileList = (fileListToAdd: File[]): void => {
		importErrorMessages.value = [];

		// Récupérer la liste actuelle des fichiers
		const newFileList = [...fileInput.value];
		const initialLength = fileInput.value.length;

		fileListToAdd.forEach((file) => {
			const importResult = isFileValid(newFileList, file);
			if (importResult === true) {
				newFileList.push(file);
			} else {
				const errorMessage = String(importResult);
				// Ne pas afficher plusieurs fois le même message d'erreur
				if (!importErrorMessages.value.includes(errorMessage)) {
					importErrorMessages.value.push(errorMessage);
				}
			}
		});

		// Si au moins un élément a été ajouté
		if (initialLength < newFileList.length) {
			// On ne modifie qu'une seule fois la liste pour n'émettre qu'un seul événement
			fileInput.value = newFileList;
		}
	};

	const errorMessages = computed<ErrorMessages>(() => {
		const messages: ErrorMessages = props.errorMessages ?? {};
		// Fallback :
		if (props.errorTypeString) {
			messages.type = props.errorTypeString;
		}

		return messages;
	});

	const isFileValid = (fileList: File[], file: File): ValidationResult => {
		const fileUploadRuleParams: IFileUploadRuleParams = {
			fileList,
			fileTypeAccepted: props.fileTypeAccepted as string[],
			maxFileNumber: props.maxFileNumber,
		};
		const fileUploadRule = fileUploadRuleFn(errorMessages.value);

		return fileUploadRule(file, fileUploadRuleParams);
	};

	watch(() => valid.value, (newValue: boolean) => {
		vFileInput.value.valid = newValue;
	}, { immediate: true });

	const onBlur = () => {
		focused.value = false;
	};

	// onChangeFlag tests false positive for the change event which is triggered a second time when the blur event is triggered. It seems to be a bug of the Vuetify Component
	let onChangeFlag = false;
	const onChange = (files: File[]) => {
		if (!onChangeFlag) {
			importFileList(files);
			emit('change', fileInput.value);
		}
		onChangeFlag = !onChangeFlag;
	};

	const onFocus = () => {
		focused.value = true;
		importErrorMessages.value = [];
	};

</script>

<style lang="scss" scoped>
.amelipro-upload {
	padding: $card-padding--tablet;
	border-radius: $card-radius;

	@media #{$media-only-xs} {
		padding: $card-padding--mobile;
	}
}
.dashed-style {
	border-style: dashed;
	border-width: thin;
}

.amelipro-upload__item {

	&:not(:last-child) {
		margin-right: 1rem;
	}
}

.truncate-text {
	white-space: nowrap;
	text-overflow: ellipsis;
	width: 148px;
	min-width: 148px;
	display: block;
	overflow: hidden
}

.v-input {
	margin-top: 0;

	& :deep(label) {
		cursor: pointer;

		&:hover {
			opacity: 0.9;
		}
	}

	& :deep(.label-outline) {
		outline: 1px dotted $ap-grey-darken1;
	}

	& :deep(.v-input__slot) {

		&::before,
		&::after {
			border: 0 !important;
		}

		& .v-file-input__text {
			display: none;
		}
	}

	& :deep(.v-input__append-inner) {
		&:not(:last-child) {
			display: none !important;
		}

		&:last-child {
			width: 100%;
		}
	}
}

.upload-input-text {
	width: calc(100% - 61px);
}

.upload-input-img {
	width: 32px;
}
</style>

